import { Link, useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import './login.css';
import logo from '../../image/logo.png';
import { useFormContext } from 'react-hook-form';
import {
  useLoginUserMutation,
  useVerifyMfaLoginMutation,
  useSendMfaEmailCodeMutation,
} from 'actions/auth/mutations';
import allIcon from '../../image/allIcon.png';
import { useDispatch } from 'react-redux';
import {
  setCredentials,
  logout,
} from 'redux/features/userSliceWithTokenValidation';
import { persistor } from 'redux/store';
import { rxdApi } from 'actions/rtkquery';
import { ACCEUIL_PATH, FORGETPASSWORD } from 'routes/navigation/navigationPaths';
import { ToastContainer, toast } from 'react-toastify';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import {
  RiBarChartBoxLine,
  RiRobotLine,
  RiShieldCheckLine,
  RiShieldKeyholeLine,
  RiMailLine,
  RiKeyLine,
  RiArrowLeftLine,
} from 'react-icons/ri';
import { GET_USER_ME_API_ROUTE } from 'routes/api/endpoints';

const FEATURES = [
  {
    icon: RiBarChartBoxLine,
    title: 'Comptabilité simplifiée',
    desc: 'Suivez revenus et dépenses en temps réel',
  },
  {
    icon: RiShieldCheckLine,
    title: 'Sécurité renforcée',
    desc: 'Vos données financières protégées',
  },
  {
    icon: RiRobotLine,
    title: 'Assistant IA intégré',
    desc: 'Extraction automatique de vos factures',
  },
];

const ConnexionForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [login] = useLoginUserMutation();
  const [verifyMfaLogin] = useVerifyMfaLoginMutation();
  const [sendMfaEmailCode] = useSendMfaEmailCodeMutation();

    const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // MFA Challenge state
  const [mfaChallenge, setMfaChallenge] = useState(null); // { mfa_required, mfa_token, email_masked }
  const [mfaCode, setMfaCode] = useState('');
  const [isUsingBackup, setIsUsingBackup] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useFormContext();

  useEffect(() => {
    // SECURITE : Purge totale au montage de l'écran de connexion
    // pour garantir qu'aucun résidu d'un compte précédent ne persiste.
    // Le purge IndexedDB est awaité : sans cela, l'utilisateur peut se
    // reconnecter avant la fin de la suppression et l'ancien profil
    // persiste alors (nom du compte précédent affiché).
    const purge = async () => {
      try {
        localStorage.clear();
        sessionStorage.clear();
        dispatch(logout());
        dispatch(rxdApi.util.resetApiState());
        await persistor.purge();
      } catch (e) {
        console.warn('Purge error on login mount:', e);
      }
    };
    purge();
  }, []);

  const completeLogin = (res) => {
    setLoading(true);
    fetch(GET_USER_ME_API_ROUTE, {
      cache: 'no-store',
      headers: {
        Authorization: `Bearer ${res.access}`,
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Profile fetch error: ${response.status}`);
        }
        return response.json();
      })
      .then(async (userData) => {
        // SECURITE : Purger completement AVANT de setter les nouvelles credentials
        // pour eviter que les anciennes donnees ne contaminent la nouvelle session
        dispatch(logout());
        dispatch(rxdApi.util.resetApiState());
        try { 
          await persistor.purge(); 
          // Attendre un tick pour que le purge soit bien complete
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (e) { /* noop */ }
        
        dispatch(setCredentials({ ...res, ...userData }));
        toast.success('Connexion réussie !');
        navigate(ACCEUIL_PATH);
      })
      .catch(async (error) => {
        console.error('Profile fetch error:', error);
        // Meme en cas d'erreur, purger avant de setter
        dispatch(logout());
        dispatch(rxdApi.util.resetApiState());
        try { 
          await persistor.purge(); 
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (e) { /* noop */ }
        
        dispatch(setCredentials({ ...res }));
        toast.success('Connexion réussie !');
        navigate(ACCEUIL_PATH);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onSubmit = (data) => {
    setLoading(true);
    login(data)
      .unwrap()
      .then((res) => {
        if (res?.mfa_required) {
          setMfaChallenge(res);
          setLoading(false);
          toast.info('Authentification en deux étapes requise.');
          return;
        }
        completeLogin(res);
      })
      .catch((error) => {
        console.log(error);
        toast.error(
          'La connexion a échoué. Vérifiez vos identifiants ou activez votre compte'
        );
        setLoading(false);
      });
  };

  const onMfaSubmit = (e) => {
    e.preventDefault();
    if (!mfaCode.trim()) {
      toast.error('Veuillez renseigner le code de validation.');
      return;
    }
    setLoading(true);
    verifyMfaLogin({
      mfa_token: mfaChallenge.mfa_token,
      code: mfaCode.trim(),
    })
      .unwrap()
      .then((res) => {
        completeLogin(res);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(
          err?.data?.detail || 'Code de vérification invalide ou expiré.'
        );
      });
  };

  const handleSendEmailCode = () => {
    if (!mfaChallenge?.mfa_token) return;
    setIsSendingEmail(true);
    sendMfaEmailCode({ mfa_token: mfaChallenge.mfa_token })
      .unwrap()
      .then((res) => {
        toast.success(res.message || 'Code envoyé avec succès par e-mail.');
      })
      .catch((err) => {
        toast.error(
          err?.data?.detail || "Échec de l'envoi de l'e-mail de vérification."
        );
      })
      .finally(() => {
        setIsSendingEmail(false);
      });
  };

  return (
    <>
      <ToastContainer />
      <div className="login-page">
        <aside className="login-brand" aria-hidden="true">
          <div className="login-brand-bg">
            <div className="login-blob login-blob-1" />
            <div className="login-blob login-blob-2" />
            <div className="login-blob login-blob-3" />
          </div>

          <div className="login-brand-inner">
            <div className="login-brand-logo">
              <img src={logo} alt="Easy Compta" />
            </div>

            <div className="login-brand-content">
              <h2 className="login-brand-title">
                La comptabilité intelligente, simplifiée
              </h2>
              <p className="login-brand-subtitle">
                Easy Compta vous accompagne au quotidien pour piloter vos
                finances avec clarté, précision et sérénité.
              </p>

              <ul className="login-features">
                {FEATURES.map(({ icon: Icon, title, desc }) => (
                  <li key={title} className="login-feature">
                    <span className="login-feature-icon">
                      <Icon />
                    </span>
                    <div className="login-feature-text">
                      <strong>{title}</strong>
                      <span>{desc}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="login-brand-illustration">
              <img src={allIcon} alt="" />
            </div>
          </div>
        </aside>

        <main className="login-form-panel">
          <div className="login-form-card">
            <div className="login-mobile-logo">
              <img src={logo} alt="Easy Compta" />
            </div>

            {mfaChallenge ? (
              <>
                <header className="login-form-header text-center">
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      background: 'rgba(0, 150, 136, 0.12)',
                      color: '#009688',
                      marginBottom: '15px',
                    }}
                  >
                    <RiShieldKeyholeLine size={32} />
                  </div>
                  <h1>Double Authentification</h1>
                  <p>
                    {isUsingBackup
                      ? "Entrez l'un de vos codes de secours d'urgence (8 caractères)."
                      : "Entrez le code à 6 chiffres généré par votre application d'authentification (Google Authenticator, Microsoft Authenticator)."}
                  </p>
                </header>

                <form onSubmit={onMfaSubmit} autoComplete="off">
                  <div className="login-field">
                    <label htmlFor="mfaCode">
                      {isUsingBackup
                        ? 'Code de secours d’urgence'
                        : 'Code de sécurité à 6 chiffres'}
                    </label>
                    <input
                      type="text"
                      id="mfaCode"
                      className="login-input"
                      style={{
                        fontSize: isUsingBackup ? '20px' : '26px',
                        letterSpacing: isUsingBackup ? '3px' : '8px',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color: '#00796b',
                      }}
                      placeholder={isUsingBackup ? 'XXXX-XXXX' : '000000'}
                      maxLength={isUsingBackup ? 12 : 6}
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value)}
                      autoFocus
                    />
                  </div>

                  <button
                    type="submit"
                    className="login-submit"
                    disabled={loading}
                  >
                    {loading ? (
                      <span
                        className="login-loader"
                        aria-label="Vérification en cours"
                      />
                    ) : (
                      'Valider et se connecter'
                    )}
                  </button>
                </form>

                <div
                  style={{
                    marginTop: '22px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    alignItems: 'center',
                  }}
                >
                  <button
                    type="button"
                    className="btn btn-link text-decoration-none p-0 text-muted"
                    style={{ fontSize: '13px' }}
                    onClick={() => {
                      setIsUsingBackup(!isUsingBackup);
                      setMfaCode('');
                    }}
                  >
                    <RiKeyLine className="me-1" />
                    {isUsingBackup
                      ? "Utiliser mon application d'authentification (TOTP)"
                      : 'Utiliser un code de secours d’urgence'}
                  </button>

                  {mfaChallenge.email_masked && (
                    <button
                      type="button"
                      className="btn btn-link text-decoration-none p-0 text-muted"
                      style={{ fontSize: '13px' }}
                      disabled={isSendingEmail}
                      onClick={handleSendEmailCode}
                    >
                      <RiMailLine className="me-1" />
                      {isSendingEmail
                        ? 'Envoi en cours...'
                        : `Recevoir un code par email (${mfaChallenge.email_masked})`}
                    </button>
                  )}

                  <button
                    type="button"
                    className="btn btn-link text-decoration-none p-0"
                    style={{
                      fontSize: '13px',
                      color: '#009688',
                      fontWeight: 600,
                      marginTop: '6px',
                    }}
                    onClick={() => {
                      setMfaChallenge(null);
                      setMfaCode('');
                    }}
                  >
                    <RiArrowLeftLine className="me-1" /> Retour à la connexion
                  </button>
                </div>
              </>
            ) : (
              <>
                <header className="login-form-header">
                  <h1>Bienvenue</h1>
                  <p>Accédez à votre espace Easy Compta</p>
                </header>

                <form
                  onSubmit={handleSubmit(onSubmit)}
                  autoComplete="off"
                  noValidate
                >
                  <div className="login-field">
                    <label htmlFor="email">Adresse email</label>
                    <input
                      type="email"
                      id="email"
                      className="login-input"
                      placeholder="vous@exemple.com"
                      {...register('email', {
                        required: 'Email obligatoire',
                      })}
                    />
                    {errors.email?.message && (
                      <div className="login-field-error">
                        {errors.email.message}
                      </div>
                    )}
                  </div>

                  <div className="login-field">
                    <label htmlFor="password">Mot de passe</label>
                    <div className="login-input-wrap">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        className="login-input login-input-password"
                        placeholder="Entrez votre mot de passe"
                        {...register('password', {
                          required: 'Le mot de passe est obligatoire',
                        })}
                      />
                      <button
                        type="button"
                        className="login-password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={
                          showPassword
                            ? 'Masquer le mot de passe'
                            : 'Afficher le mot de passe'
                        }
                      >
                        {showPassword ? <FaEye /> : <FaEyeSlash />}
                      </button>
                    </div>
                    {errors.password?.message && (
                      <div className="login-field-error">
                        {errors.password.message}
                      </div>
                    )}
                  </div>

                  <div className="login-options">
                    <label className="login-remember" htmlFor="remember">
                      <input type="checkbox" id="remember" />
                      Se souvenir de moi
                    </label>
                    <Link className="login-forgot" to={FORGETPASSWORD}>
                      Mot de passe oublié ?
                    </Link>
                  </div>

                  <button
                    type="submit"
                    className="login-submit"
                    disabled={loading}
                  >
                    {loading ? (
                      <span
                        className="login-loader"
                        aria-label="Connexion en cours"
                      />
                    ) : (
                      'Se connecter'
                    )}
                  </button>
                </form>

                <p className="login-footer">
                  Vous n&apos;avez pas de compte ?{' '}
                  <Link to="/signup">Créer votre compte</Link>
                </p>
              </>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default ConnexionForm;

